import {
  fetchLegalDocument,
  fetchLegalDocuments,
  type LegalDocumentApiType,
} from '@/services/get-legal-documents';
import { useQuery } from '@tanstack/react-query';

export const legalDocumentsKeys = {
  all: ['legal-documents'] as const,
  list: () => [...legalDocumentsKeys.all, 'list'] as const,
  detail: (type: LegalDocumentApiType) => [...legalDocumentsKeys.all, type] as const,
};

/**
 * Legal documents rarely change, and a stale privacy policy for a few minutes is harmless —
 * so both queries hold their data far longer than the app default rather than refetching on
 * every visit to Settings.
 */
const LEGAL_STALE_TIME = 60 * 60 * 1000;

/** Every legal document from `GET /api/v1/legal-documents`. */
export function useLegalDocumentsQuery() {
  return useQuery({
    queryKey: legalDocumentsKeys.list(),
    queryFn: () => fetchLegalDocuments(),
    staleTime: LEGAL_STALE_TIME,
  });
}

/**
 * One legal document from `GET /api/v1/legal-documents/{type}`.
 *
 * `enabled` exists because the endpoint needs a bearer token: the pre-auth screens show the
 * same documents and must not fire a request that can only 401.
 */
export function useLegalDocumentQuery(type: LegalDocumentApiType, enabled = true) {
  return useQuery({
    queryKey: legalDocumentsKeys.detail(type),
    queryFn: () => fetchLegalDocument(type),
    staleTime: LEGAL_STALE_TIME,
    enabled,
  });
}
